import { IonIcon } from '@ionic/react';
import { PlayerEvent } from '@lottiefiles/react-lottie-player';
import { pause, play } from 'ionicons/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  ContentContainer,
  ItemContainer,
  ItemText,
  List,
  PlayIcon,
  StyledPlayer,
  WaveformImage,
} from './DominateVoiceItem.styles';

interface VoiceStyleItemProps {
  title: string;
  isSelected?: boolean;
  onClick: () => void;
  audioSrc: string;
}

export const DominateVoiceList: React.FC<{
  children?: NonNullable<React.ReactNode>;
  className?: string;
}> = ({ children, className }) => {
  return <List className={className}>{children}</List>;
};

export const DominateVoiceItem: React.FC<VoiceStyleItemProps> = ({
  title,
  isSelected,
  onClick,
  audioSrc,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lottiePlayerRef = useRef<any>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const prevIsSelectedRef = useRef(isSelected);

  const handleAudioEnded = useCallback(() => {
    setIsEnded(true);
    setIsPaused(true);
    if (lottiePlayerRef.current) {
      lottiePlayerRef.current.stop();
    }
  }, []);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioSrc);
      audioRef.current.preload = 'auto';
    }

    if (audioRef.current.src !== audioSrc) {
      audioRef.current.src = audioSrc;
    }

    if (audioRef.current) {
      audioRef.current.removeEventListener('ended', handleAudioEnded);
      audioRef.current.addEventListener('ended', handleAudioEnded);
      audioRef.current.onended = handleAudioEnded;
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', handleAudioEnded);
        audioRef.current.onended = null;
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, [audioSrc, handleAudioEnded]);

  useEffect(() => {
    if (!audioRef.current || !isSelected || isPaused) return;

    const checkTimeInterval = setInterval(() => {
      if (audioRef.current) {
        if (
          audioRef.current.duration > 0 &&
          audioRef.current.currentTime > 0 &&
          audioRef.current.duration - audioRef.current.currentTime < 0.1
        ) {
          handleAudioEnded();
          clearInterval(checkTimeInterval);
        }
      }
    }, 100);

    return () => {
      clearInterval(checkTimeInterval);
    };
  }, [isSelected, isPaused, handleAudioEnded]);

  useEffect(() => {
    if (!audioRef.current) return;

    if (isEnded && isSelected && !isPaused) {
      audioRef.current.currentTime = 0;
      setIsEnded(false);
    }

    if (isSelected && !prevIsSelectedRef.current) {
      setIsPaused(false);
      setIsEnded(false);
      audioRef.current.currentTime = 0;
      audioRef.current
        .play()
        .catch((e) => console.error('Ошибка воспроизведения:', e));
    } else if (isSelected) {
      if (!isPaused) {
        audioRef.current
          .play()
          .catch((e) => console.error('Ошибка воспроизведения:', e));
      } else {
        audioRef.current.pause();
      }
    } else if (!isSelected && prevIsSelectedRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPaused(false);
      setIsEnded(false);
    }

    prevIsSelectedRef.current = isSelected;
  }, [isSelected, isPaused, isEnded]);

  useEffect(() => {
    if (!lottiePlayerRef.current) return;

    if (isSelected && !isPaused && !isEnded) {
      lottiePlayerRef.current.play();
    } else {
      lottiePlayerRef.current.pause();
    }
  }, [isSelected, isPaused, isEnded]);

  const handleClick = () => {
    if (isSelected) {
      if (isEnded) {
        setIsEnded(false);
      }
      setIsPaused(!isPaused);
    } else {
      onClick();
    }
  };

  return (
    <ItemContainer onClick={handleClick} $selected={isSelected}>
      <PlayIcon>
        <IonIcon icon={isSelected && !isPaused && !isEnded ? pause : play} />
      </PlayIcon>
      <ContentContainer>
        <ItemText $selected={isSelected}>{title}</ItemText>
        {isSelected ? (
          <StyledPlayer
            ref={lottiePlayerRef}
            autoplay={!isPaused && !isEnded}
            keepLastFrame
            loop
            src="/assets/layout-own-ai-dominate/voice_style/audio-wave-animation.json"
            onEvent={(event: PlayerEvent) => {
              if (event === 'ready') {
                if (isPaused || isEnded) {
                  lottiePlayerRef.current?.pause();
                }
              }
            }}
          />
        ) : (
          <WaveformImage
            src="/assets/layout-own-ai-dominate/voice_style/default-voice-line.png"
            alt="Voice waveform"
          />
        )}
      </ContentContainer>
    </ItemContainer>
  );
};
